// What a list card (Forecast Manager, phone list, the chat's earlier-forecast lines) reads a
// forecast from.
//
// A lean-loaded row has no `contract` and an empty `history` until its detail arrives, so
// answerSummary, resolvedOutcome and liveContractMomentOf would find no answer, no resolution
// date and no reforecast moment. The lean list carries those keys as `listSummary`; this puts
// them where those readers look. A row whose detail has landed is returned as it is: its own
// contract and history are newer than a summary taken at load. A lean row that has gained a
// contract without its history (a run that finished in the background) keeps its own contract
// and reads the summary's history, with the summary's contract (the answer the run replaced)
// as its latest entry, so the card's change is measured against it, even when the new answer
// is the same.
//
// The summary is the row as stored, so it can still hold a vintage a run fired after the
// question resolved. Detail hydration steps back from it (settledRun); the card applies the same
// rule to its own copy, leaving the run's real contract, history and save guards alone.
import { rollbackPostResolutionVintage } from "./postResolutionRuns.js";

/**
 * The history entry for the answer a background run replaced: generated when the summary's
 * last entry was replaced (or at creation), replaced when the run began.
 */
function replacedVintage(f, contract, history) {
  const last = history.length ? history[history.length - 1] : null;
  const entry = { contract };
  const generatedAt = last?.snapshotAt ?? f.createdAt ?? null;
  const snapshotAt = f.lastReforecastedAt ?? f.startedAt ?? null;
  if (generatedAt) entry.generatedAt = generatedAt;
  if (snapshotAt) entry.snapshotAt = snapshotAt;
  return entry;
}

/**
 * @param {object|null|undefined} f a forecast row or run
 * @returns {object|null|undefined} `f`, or a copy whose `contract` / `history` are the summary's
 */
export function listSource(f) {
  if (!f || !f.listSummary || (f.contract && !f.detailPending)) return f;
  const { contract, history } = f.listSummary;
  const ownHistory = Array.isArray(f.history) && f.history.length ? f.history : null;
  if (f.contract && ownHistory) return f;
  const summaryContract = contract && typeof contract === "object" ? contract : null;
  const summaryHistory = Array.isArray(history) ? history : [];
  const source = {
    ...f,
    contract: f.contract || summaryContract,
    history:
      ownHistory ||
      (f.contract && summaryContract
        ? [...summaryHistory, replacedVintage(f, summaryContract, summaryHistory)]
        : summaryHistory),
  };
  const rollback = rollbackPostResolutionVintage(source);
  if (!rollback) return source;
  return {
    ...source,
    contract: rollback.contract,
    history: rollback.history,
    lastReforecastedAt: rollback.lastReforecastedAt,
  };
}
